import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Snackbar,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth0 } from '@auth0/auth0-react';
import { UserRole } from '../../types/role';
import {
  getBarberSubscriptionByBarberId,
  getShopSubscriptionByShopId,
  retrieveStripeSubscription,
} from '../../middleware/registration_service/getRequests';
import {
  updateBarberSubscription,
  updateShopSubscription,
} from '../../middleware/registration_service/updateRequest';
import { cancelSubscription } from '../../middleware/registration_service/createRequest';
import { StripeSubscription } from '../../types/stripePayments';

export default function CancelSubscription() {
  const { getAccessTokenSilently } = useAuth0();
  const role = localStorage.getItem('role');
  const isShopOwner = role === UserRole.SHOP_OWNER;
  const isBarber = role === UserRole.BARBER;

  /**
   * Looks up the live status of a Stripe subscription so the Account Page
   * always reflects Stripe's current state rather than the local record's
   * possibly-stale cached `status`.
   */
  const fetchLiveStripeStatus = async (
    stripeSubId: string | null,
    token: string
  ): Promise<StripeSubscription['status'] | null> => {
    if (!stripeSubId) return null;
    try {
      const response = await retrieveStripeSubscription(stripeSubId, token);
      const stripeSubscription: StripeSubscription = response.data;
      return stripeSubscription?.status ?? null;
    } catch (err) {
      console.warn('Could not retrieve live Stripe subscription status:', err);
      return null;
    }
  };

  const [subscriptionId, setSubscriptionId] = useState<number | null>(null);
  const [subscriptionName, setSubscriptionName] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    null
  );
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [subscriptionPrice, setSubscriptionPrice] = useState<number | null>(
    null
  );
  const [barberSeatCount, setBarberSeatCount] = useState<number | null>(null);
  const [marketingCampaignCount, setMarketingCampaignCount] = useState<
    number | null
  >(null);
  const [emailNotificationCount, setEmailNotificationCount] = useState<
    number | null
  >(null);
  const [smsNotificationCount, setSmsNotificationCount] = useState<
    number | null
  >(null);
  const [expiryDate, setExpiryDate] = useState<string | null>(null);
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState<
    string | null
  >(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      setLoadingSubscription(true);
      try {
        const token = await getAccessTokenSilently();
        if (isShopOwner) {
          const shopIdItem = localStorage.getItem('reg_shop_id');
          if (!shopIdItem) return;
          const res = await getShopSubscriptionByShopId(
            BigInt(shopIdItem),
            token
          );
          if (res.data) {
            setSubscriptionId(res.data.id ?? null);
            setSubscriptionName(res.data.name ?? null);
            setSubscriptionPrice(res.data.price ?? null);
            setBarberSeatCount(res.data.barberSeatCount ?? null);
            setMarketingCampaignCount(res.data.marketingCampaignCount ?? null);
            setEmailNotificationCount(res.data.emailNotificationCount ?? null);
            setSmsNotificationCount(res.data.smsNotificationCount ?? null);
            setExpiryDate(
              res.data.expiryDate
                ? res.data.expiryDate.toString().slice(0, 10)
                : null
            );
            setStripeSubscriptionId(res.data.stripeSubscriptionId ?? null);
            setProductId(res.data.productId ?? null);
            setCustomerId(res.data.customerId ?? undefined);
            const liveStatus = await fetchLiveStripeStatus(
              res.data.stripeSubscriptionId ?? null,
              token
            );
            setSubscriptionStatus(liveStatus ?? res.data.status ?? null);
          }
        } else if (isBarber) {
          const partnerItem = localStorage.getItem('reg_partner_id');
          if (!partnerItem) return;
          const res = await getBarberSubscriptionByBarberId(
            BigInt(partnerItem),
            token
          );
          if (res.data) {
            setSubscriptionId(res.data.id ?? null);
            setSubscriptionName(res.data.name ?? null);
            setSubscriptionPrice(res.data.price ?? null);
            setMarketingCampaignCount(res.data.marketingCampaignCount ?? null);
            setEmailNotificationCount(res.data.emailNotificationCount ?? null);
            setSmsNotificationCount(res.data.smsNotificationCount ?? null);
            setExpiryDate(
              res.data.expiryDate
                ? res.data.expiryDate.toString().slice(0, 10)
                : null
            );
            setStripeSubscriptionId(res.data.stripeSubscriptionId ?? null);
            setProductId(res.data.productId ?? null);
            setCustomerId(res.data.customerId ?? null);
            const liveStatus = await fetchLiveStripeStatus(
              res.data.stripeSubscriptionId ?? null,
              token
            );
            setSubscriptionStatus(liveStatus ?? res.data.status ?? null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch subscription for cancellation:', err);
      } finally {
        setLoadingSubscription(false);
      }
    };

    if (isShopOwner || isBarber) {
      fetchSubscription();
    } else {
      setLoadingSubscription(false);
    }
  }, [isShopOwner, isBarber, getAccessTokenSilently]);

  const handleCancelConfirm = async () => {
    if (subscriptionId === null) {
      setError('Subscription ID not found. Unable to cancel.');
      setDialogOpen(false);
      return;
    }
    setCancelling(true);
    setError(null);
    try {
      // Cancel Stripe subscription first
      let cancelStatus:
        | 'incomplete'
        | 'incomplete_expired'
        | 'trialing'
        | 'active'
        | 'past_due'
        | 'canceled'
        | 'unpaid'
        | 'paused' = 'canceled';
      let stripeCancelSuccess = true;
      if (stripeSubscriptionId) {
        try {
          const token = await getAccessTokenSilently();
          const cancelResponse = await cancelSubscription(
            stripeSubscriptionId,
            token
          );
          cancelStatus = cancelResponse.data?.status ?? 'canceled';
          console.log('Cancel subscription response:', cancelResponse.data);
        } catch (stripeCancelErr) {
          console.warn(
            'Could not cancel Stripe subscription:',
            stripeCancelErr
          );
          stripeCancelSuccess = false;
        }
      }

      if (!stripeCancelSuccess) {
        setError('Failed to cancel Stripe subscription. Please try again.');
        setDialogOpen(false);
        return;
      }

      if (!stripeSubscriptionId) {
        setError('Subscription ID not found. Unable to cancel.');
        setDialogOpen(false);
        return;
      }

      if (isShopOwner) {
        const token = await getAccessTokenSilently();
        const shopIdItem = localStorage.getItem('reg_shop_id');
        if (!shopIdItem) {
          setError('Shop ID not found.');
          return;
        }
        await updateShopSubscription(
          subscriptionId,
          {
            name: subscriptionName ?? undefined,
            price: subscriptionPrice ?? undefined,
            barberSeatCount: barberSeatCount ?? undefined,
            marketingCampaignCount: marketingCampaignCount ?? undefined,
            emailNotificationCount: emailNotificationCount ?? undefined,
            smsNotificationCount: smsNotificationCount ?? undefined,
            shopId: shopIdItem,
            expiryDate: expiryDate ?? undefined,
            status: cancelStatus,
            productId: productId ?? undefined,
            customerId: customerId,
            stripeSubscriptionId: stripeSubscriptionId,
          },
          token
        );
      } else if (isBarber) {
        const token = await getAccessTokenSilently();
        const partnerItem = localStorage.getItem('reg_partner_id');
        if (!partnerItem) {
          setError('Barber ID not found.');
          return;
        }
        await updateBarberSubscription(
          subscriptionId,
          {
            name: subscriptionName ?? undefined,
            price: subscriptionPrice ?? undefined,
            marketingCampaignCount: marketingCampaignCount ?? undefined,
            emailNotificationCount: emailNotificationCount ?? undefined,
            smsNotificationCount: smsNotificationCount ?? undefined,
            barberId: partnerItem,
            expiryDate: expiryDate ?? undefined,
            status: cancelStatus,
            productId: productId ?? undefined,
            customerId: customerId,
            stripeSubscriptionId: stripeSubscriptionId,
          },
          token
        );
      }
      setSubscriptionStatus('canceled');
      setDialogOpen(false);
      setSuccessOpen(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to cancel subscription. Please try again.'
      );
    } finally {
      setCancelling(false);
    }
  };

  if (loadingSubscription) {
    return (
      <Container sx={{ mt: '1em', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Container>
    );
  }

  const alreadyCancelled = subscriptionStatus === 'canceled';

  return (
    <Container sx={{ mt: '1em' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current subscription info */}
      {subscriptionName && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: alreadyCancelled ? '#fff3e0' : '#f5f5f5',
            borderRadius: 1,
            border: `1px solid ${alreadyCancelled ? '#ffcc02' : '#e0e0e0'}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Current Plan: {subscriptionName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Status:{' '}
            <span
              style={{
                color: alreadyCancelled ? '#e65100' : '#2e7d32',
                fontWeight: 'bold',
              }}
            >
              {alreadyCancelled ? 'Canceled' : 'Active'}
            </span>
          </Typography>
        </Box>
      )}

      {!subscriptionId && !loadingSubscription && (
        <Alert severity="info">No active subscription found.</Alert>
      )}

      {alreadyCancelled ? (
        <Alert severity="warning">
          Your subscription has already been cancelled. Visit the{' '}
          <strong>Subscription Manager</strong> page to reactivate.
        </Alert>
      ) : (
        <>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}
          >
            <WarningAmberIcon color="warning" sx={{ mt: 0.3 }} />
            <Typography variant="body2" color="text.secondary">
              Cancelling your subscription will remove access to key features
              including Schedule Manager and Calendar Manager at the end of your
              current billing period.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="error"
            fullWidth
            disabled={!subscriptionId}
            onClick={() => setDialogOpen(true)}
          >
            Cancel Subscription
          </Button>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your{' '}
            <strong>{subscriptionName || 'current'}</strong> subscription? You
            will lose access to premium features at the end of your billing
            period. This action can be reversed by reactivating your
            subscription.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={cancelling}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={
              cancelling ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={5000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Your subscription has been cancelled successfully.
        </Alert>
      </Snackbar>
    </Container>
  );
}
